import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, filter, map, Observable, Subject } from 'rxjs';
import { MapGroup } from '../shared/classes/map-group';
import { MapLayer } from '../shared/classes/map-layer';
import Map from 'ol/Map';
import { Tile as TileLayer, Image as ImageLayer } from 'ol/layer';
import BaseLayer from 'ol/layer/Base';
import { TileWMS, ImageWMS } from 'ol/source';
import { Coordinate } from 'ol/coordinate';
import Point from 'ol/geom/Point';

type ZoomConfig = { mapId?: string, coordinate: Coordinate };

@Injectable({
  providedIn: 'root',
})
export class MapService {
  private http = inject(HttpClient);
  private static readonly MAP_FIT_PADDING: [number, number, number, number] = [100, 100, 100, 100];
  private static readonly MAP_FIT_DURATION_MS = 1000;
  private static readonly MAP_FIT_MAX_ZOOM = 14;
  private goToPointHandlingInitialized = false;


  public goToPoint$ = new Subject<ZoomConfig | Coordinate>();


  public pointSelectionActive: boolean = false;

  static mapRefs: { [key: string]: Map } = {};

  static getMapRef(id: string = 'tgridBaseMap'): Map | undefined {
    const map = MapService.mapRefs[id];
    if (!map) {
      console.error(`No map found with id: ${id}`);
      return undefined;
    }
    return map;
  }

  clearActiveLayers: Subject<void> = new Subject();

  public activeIdentifyLayers = new BehaviorSubject<MapLayer[]>([]);

  constructor() {
  }

  public initializeGoToPointHandling(): void {
    if (this.goToPointHandlingInitialized) {
      return;
    }

    this.goToPointHandlingInitialized = true;
    this.goToPoint$.pipe(
      filter(() => this.pointSelectionActive),
    ).subscribe(configOrCoordinate => {
      let zoomConfig = configOrCoordinate as ZoomConfig;
      if (Array.isArray(configOrCoordinate)) {
        zoomConfig = {
          mapId: 'tgridBaseMap',
          coordinate: configOrCoordinate,
        };
      }
      const mapRef = MapService.getMapRef(zoomConfig.mapId);
      if (!mapRef) {
        return;
      }
      const view = mapRef.getView();
      view.fit(new Point(zoomConfig.coordinate), {
        padding: MapService.MAP_FIT_PADDING,
        duration: MapService.MAP_FIT_DURATION_MS,
        maxZoom: MapService.MAP_FIT_MAX_ZOOM,
      });
    });
  }

  getMapLayerGroups(): Observable<MapGroup[]> {
    const endpoint = '/api/maplayer/groups';
    return this.http.get<MapGroup[]>(endpoint).pipe(
      map(groups => groups.map(group => new MapGroup(group))),
    );
  }


  public isLayerOnMap(layer: BaseLayer, mapId: string = 'tgridBaseMap'): boolean {
    const mapRef = MapService.getMapRef(mapId);
    return mapRef?.getLayers().getArray().includes(layer) ?? false;
  }

  public createTileSource(mapLayer: MapLayer, defaultOpacity = 80): TileLayer<TileWMS> | ImageLayer<ImageWMS> {
    let extraParams: { [key: string]: string | number | boolean } = {};
    if (mapLayer.params) {

      try {
        extraParams = JSON.parse(mapLayer.params);
      } catch (error) {
        console.error(`Invalid layer params JSON for layer ${mapLayer.displayName}:`, error);
        extraParams = {};
      }
    }

    const layerConfig = {
      properties: {
        name: mapLayer.displayName,
      },
      opacity: defaultOpacity / 100,
    };

    const sourceProperties = {
      url: mapLayer.serviceUrl,
      params: { 'LAYERS': mapLayer.serviceLayer, ...extraParams },
      attributions: mapLayer.buildAttribution(),
    };


    if (extraParams?.['singleImage']) {
      return new ImageLayer({
        ...layerConfig,
        source: new ImageWMS(sourceProperties),
      });
    }

    return new TileLayer({
      ...layerConfig,
      source: new TileWMS(sourceProperties),
    });

  }

  public exportMapAsImage(mapId: string = 'tgridBaseMap', fileName: string = 'map') {
    const mapRef = MapService.getMapRef(mapId);
    if (!mapRef) {
      return;
    }

    const size = mapRef.getSize();
    if (!size) {
      console.error(`Map size unavailable for map id: ${mapId}`);
      return;
    }

    const mapCanvas = document.createElement('canvas');
    mapCanvas.width = size[0];
    mapCanvas.height = size[1];
    const mapContext = mapCanvas.getContext('2d');
    if (!mapContext) {
      console.error('Unable to get canvas 2D context for map export');
      return;
    }

    // Draw map layers
    Array.from(document.querySelectorAll('.ol-layer canvas')).forEach((canvas) => {
      if (( canvas as HTMLCanvasElement ).width > 0) {
        const opacity = (canvas.parentNode as HTMLElement).style.opacity || '1';
        mapContext.globalAlpha = parseFloat(opacity);
        mapContext.drawImage(canvas as HTMLCanvasElement, 0, 0);
      }
    });

    // Draw attributions
    mapContext.globalAlpha = 1;
    const attribution = document.querySelector('.ol-attribution');
    if (attribution) {
      const rect = attribution.getBoundingClientRect();
      mapContext.fillStyle = 'rgba(255, 255, 255, 0.8)';
      mapContext.fillRect(0, size[1] - rect.height - 5, rect.width + 10, rect.height + 5);
      mapContext.fillStyle = '#000';
      mapContext.font = '12px sans-serif';
      mapContext.fillText(attribution.textContent || '', 5, size[1] - 10);
    }

    // Download
    mapCanvas.toBlob((blob) => {
      if (blob) {
        const link = document.createElement('a');
        link.download = fileName;
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
      }
    });
  }

}
